import signals from 'signals';
import { Application, Text } from 'pixi.js';
import {
  COLOR,
  EASE,
  ELEMENT_MAIN,
  IMAGES,
  MainSignals,
  PATH,
  SIZE,
  END_STEPS,
  BASE_FONT,
  IS_MOBILE,
} from './settings/constants';
import Resizer from './helpers/Resizer';
import Scenes from './components/scenes/Scenes';
import { getSizeData, ResizeData } from './helpers/ResizeData';
import Texts from './components/texts/Texts';
import UI from './components/ui/UI';
import { preloadSounds } from './helpers/SoundManager';
import WelcomeScreen from './components/welcome/WelcomeScreen';
import FlipInfo from './components/flipInfo/FlipInfo';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import Scroller from './helpers/Scroller';
import { getCurrentScrollValue } from './helpers/Scroll';
export default class Main {
  signals: MainSignals;
  resizer?: Resizer;

  sizeData = getSizeData();

  welcomeScreen: WelcomeScreen;
  mainElement: HTMLDivElement;
  mainCanvas: HTMLCanvasElement;
  pixi: Application;

  scrollActual = 0;
  scrollGoal = 0;

  constructor() {
    // helpers
    this.signals = this.getSignals();

    // screens
    this.welcomeScreen = new WelcomeScreen(this.setMainContent.bind(this));
    new FlipInfo();

    // // pixi
    this.mainElement = this.getMainElement();
    this.mainCanvas = this.getCanvas();
    this.pixi = this.getPixi();

    // load assets
    this.loadFonts();
  }

  // --------------------------------------------------------------
  // Assets
  // --------------------------------------------------------------

  loadFonts(): void {
    // Note: Currently unused - loading fonts directly in index
    // WebFont.load({
    //   active: () => {
    //     setTimeout(() => {
    //       this.initFonts();
    //       this.loadSounds();
    //     }, 100);
    //   },
    //   google: {
    //     families: ['Open Sans'],
    //   },
    // });
    this.initFonts();
    this.loadSounds();
  }

  loadSounds(): void {
    preloadSounds();
    this.loadAssets();
  }

  loadAssets(): void {
    // images
    const images = Object.entries(IMAGES);
    images.forEach((imageData: string[]) => {
      this.pixi.loader.add(imageData[1], `${PATH.IMAGE}${imageData[1]}`);
    });

    // end steps
    for (let s = 0; s < END_STEPS; s++) {
      this.pixi.loader.add(`s-${s}`, `${PATH.IMAGE}steps/s-${s}.jpg`);
    }

    // sheets
    this.pixi.loader.add(`${PATH.IMAGE}sheets/sheet.json`);
    this.pixi.loader.onProgress.add(() => {
      this.welcomeScreen.loaderProgress(this.pixi.loader.progress);
    });
    this.pixi.loader.load(() => {
      this.loadEnd();
    });
  }

  loadEnd(): void {
    this.welcomeScreen.loadingComplete();
  }

  // --------------------------------------------------------------
  // Signals
  // --------------------------------------------------------------

  getSignals(): MainSignals {
    return {
      tickCall: new signals.Signal(),
      stateCall: new signals.Signal(),
      resizeCall: new signals.Signal(),
      moveCall: new signals.Signal(),
    };
  }

  // --------------------------------------------------------------
  // Start
  // --------------------------------------------------------------

  setMainContent(): void {
    new Scroller();
    this.mainCanvas.style.display = 'block';
    this.setMainParts();
    this.setAbout();
    this.setResizer();
    this.setInteraction();
    this.setScroll();
    this.setLoop();
  }

  // --------------------------------------------------------------
  // About
  // --------------------------------------------------------------

  setAbout(): void {
    const aboutCLoseBtn = document.getElementById('btn-about-close');
    const aboutLayer = document.getElementById('o-projektu');

    if (aboutCLoseBtn) {
      aboutCLoseBtn.addEventListener('click', () => {
        if (aboutLayer) {
          aboutLayer.style.display = 'none';
        }
      });
    }
  }

  // --------------------------------------------------------------
  // Resize
  // --------------------------------------------------------------

  setResizer(): void {
    this.signals.resizeCall.add(this.resizeCall, this);
    this.resizer = new Resizer(this.signals.resizeCall);
  }

  resizeCall(data: ResizeData): void {
    this.sizeData = data;
    this.pixi.renderer.resize(data.window.width, data.window.height);

    this.mainElement.style.width = `${data.window.width}px`;
    this.mainElement.style.height = `${SIZE.SCROLL * data.stage.scale}px`;

    this.mainCanvas.style.width = `${data.window.width}px`;
    this.mainCanvas.style.height = `${data.window.height}px`;
  }

  // --------------------------------------------------------------
  // Canvas + Pixi
  // --------------------------------------------------------------

  getMainElement(): HTMLDivElement {
    const element = document.getElementById(ELEMENT_MAIN) as HTMLDivElement;
    element.style.display = 'block';
    element.style.position = 'relative';
    element.style.overflow = 'hidden';
    return element;
  }

  getCanvas(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    this.mainElement.appendChild(canvas);
    canvas.style.display = 'block';
    canvas.style.position = 'fixed';
    canvas.style.overflow = 'hidden';
    canvas.style.top = '0px';
    canvas.style.left = '0px';
    canvas.style.display = 'none';
    return canvas;
  }

  getPixi(): Application {
    // init pixi
    const rendererOptions = {
      antialias: true,
      transparent: false,
      resolution: 1,
      view: this.mainCanvas,
      backgroundColor: COLOR.BG_CANVAS,
    };
    return new Application(rendererOptions);
  }

  // --------------------------------------------------------------
  // Interaction
  // --------------------------------------------------------------

  setInteraction(): void {
    if (IS_MOBILE) {
      this.pixi.renderer.plugins.interaction.autoPreventDefault = false;
      this.pixi.renderer.view.style.touchAction = 'auto';
    } else {
      this.pixi.stage.interactive = true;
      this.pixi.stage.on('pointermove', this.handleMove.bind(this));
    }
  }

  handleMove(event: any): void {
    const { x, y } = event.data.getLocalPosition(this.pixi.stage);
    const { width, height } = this.sizeData.window;
    const posX = Math.min(width, Math.max(0, x));
    const posY = Math.min(height, Math.max(0, y));

    this.signals.moveCall.dispatch({
      x: posX / width,
      y: posY / height,
    });
  }

  // --------------------------------------------------------------
  // Scroll
  // --------------------------------------------------------------

  setScroll(): void {
    window.addEventListener('scroll', this.handleScroll.bind(this));
  }

  handleScroll(): void {
    this.scrollGoal = getCurrentScrollValue();
    // For scroll position debug
    // console.log(this.scrollGoal);
  }

  // --------------------------------------------------------------
  // Tick
  // --------------------------------------------------------------

  setLoop(): void {
    this.pixi.ticker.add(this.tick.bind(this));
  }

  tick(): void {
    // scroll ease
    this.scrollActual += (this.scrollGoal - this.scrollActual) * EASE.SCROLL;
    // send update
    this.signals.tickCall.dispatch({
      ms: this.pixi.ticker.elapsedMS,
      scroll: this.scrollActual,
    });
  }

  // --------------------------------------------------------------
  // Main content
  // --------------------------------------------------------------

  setMainParts(): void {
    this.pixi.stage.addChild(new Scenes(this.signals));
    this.pixi.stage.addChild(new Texts(this.signals));
    this.pixi.stage.addChild(new UI(this.signals));
  }

  initFonts(): void {
    const initString = 'qwertzuiopasdfghjklyxcvbnmQWERTZUIOPASDFGHJKLYXCVBNMěščřžýáíéúůĚŠČŘŽÝÁÍÉÚŮ';
    // fonts init fix
    const tempNormalLabel = new Text(initString, {
      fontFamily: BASE_FONT,
      fontSize: 10,
      fill: 0xffffff,
    });
    tempNormalLabel.anchor.x = 0.5;
    tempNormalLabel.alpha = 0.01;
    this.pixi.stage.addChild(tempNormalLabel);

    const tempItalicLabel = new Text(initString, {
      fontFamily: BASE_FONT,
      fontStyle: 'italic',
      fontSize: 10,
      fill: 0xffffff,
    });
    tempItalicLabel.anchor.x = 0.5;
    tempItalicLabel.alpha = 0.01;
    this.pixi.stage.addChild(tempItalicLabel);
  }
}

// --------------------------------------------------------------
// Init
// --------------------------------------------------------------

gsap.registerPlugin(ScrollToPlugin);

new Main();
